import { type ReactNode, useState } from "react";
import { Button } from "@/shared/components/ui/button";
import { Textarea } from "@/shared/components/ui/textarea";
import { Check, Edit3, X } from "lucide-react";
import { formatDateTime } from "@/shared/lib/utils";
import { DetailHeaderMetadataItem } from "./detail-header-metadata-item";

export type DetailHeaderInfoProps = {
  title?: string;
  titleFallback?: string;
  status?: string;
  type?: string;
  description?: string;
  createdAt?: string;
  updatedAt?: string;
  onUpdateDescription?: (description: string) => Promise<void>;
  onAfterDescriptionUpdate?: () => void;
  titleAddon?: ReactNode;
  descriptionPlaceholder?: string;
  emptyDescriptionLabel?: string;
  isDescriptionEditable?: boolean;
};

export function DetailHeaderInfo({
  title,
  titleFallback = "Details",
  status,
  type,
  description,
  createdAt,
  updatedAt,
  onUpdateDescription,
  onAfterDescriptionUpdate,
  titleAddon,
  descriptionPlaceholder = "Enter a description...",
  emptyDescriptionLabel = "No description provided",
  isDescriptionEditable = true,
}: DetailHeaderInfoProps) {
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [descriptionDraft, setDescriptionDraft] = useState("");

  const handleEditDescription = () => {
    setDescriptionDraft(description || "");
    setIsEditingDescription(true);
  };

  const handleSaveDescription = async () => {
    if (onUpdateDescription) {
      await onUpdateDescription(descriptionDraft);
      onAfterDescriptionUpdate?.();
    }
    setIsEditingDescription(false);
  };

  const handleCancelEdit = () => {
    setIsEditingDescription(false);
    setDescriptionDraft("");
  };

  const formattedCreated = formatDateTime(createdAt);
  const formattedUpdated = formatDateTime(updatedAt);
  const hasType = Boolean(type);
  const hasUpdated = Boolean(formattedUpdated);
  const hasCreated = Boolean(formattedCreated);
  const canEditDescription =
    isDescriptionEditable && Boolean(onUpdateDescription);

  return (
    <div className="flex flex-col gap-2">
      <div className="flex w-full items-center gap-2">
        <h1 className="min-w-0 text-2xl font-semibold tracking-tight">
          {title ?? titleFallback}
        </h1>
        {titleAddon}
      </div>
      <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
        {status && (
          <DetailHeaderMetadataItem
            label="Status"
            value={status}
            isStatus
            showSeparator={hasType || hasUpdated || hasCreated}
          />
        )}
        {type && (
          <DetailHeaderMetadataItem
            label="Type"
            value={type}
            showSeparator={hasUpdated || hasCreated}
          />
        )}
        {formattedUpdated && (
          <DetailHeaderMetadataItem
            label="Last updated"
            value={formattedUpdated}
            showSeparator={hasCreated}
          />
        )}
        {formattedCreated && (
          <DetailHeaderMetadataItem label="Created" value={formattedCreated} />
        )}
      </div>
      {isEditingDescription ? (
        <div className="mt-2 space-y-2">
          <Textarea
            value={descriptionDraft}
            onChange={(e) => setDescriptionDraft(e.target.value)}
            placeholder={descriptionPlaceholder}
            rows={3}
            className="text-sm"
          />
          <div className="flex gap-2">
            <Button size="sm" onClick={handleSaveDescription}>
              <Check className="mr-1 h-4 w-4" />
              Save
            </Button>
            <Button size="sm" variant="outline" onClick={handleCancelEdit}>
              <X className="mr-1 h-4 w-4" />
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <div className="mt-0.5 flex items-start gap-2">
          <p className="flex-1 italic text-sm text-muted-foreground">
            {description || emptyDescriptionLabel}
          </p>
          {canEditDescription && status?.toLowerCase() !== "in progress" && (
            <Button
              aria-label="Edit description"
              size="sm"
              variant="ghost"
              onClick={handleEditDescription}
              className="h-6 w-6 p-0"
            >
              <Edit3 className="h-3 w-3" />
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
